import { loadAndCacheImage } from "@cornerstonejs/core/loaders/imageLoader";
import { getViewport } from "./CornerstoneViewportService";
import {
  CONSTRUCTABLE_MODALITIES,
  MAX_MPR_VOLUME_BYTES,
  PREFETCH_IMAGE_CONCURRENCY,
} from "../../../constants/Constants";
import { metaData } from "@cornerstonejs/core";
import { sortSeries } from "./sortSeriesDataByPosition";
import { getThumbnailViewport } from "./CornerstoneThumbnailService";

const reconstructionIssues = {
  MISSING_FRAMES: "missingframes",
  IRREGULAR_SPACING: "irregularspacing",
  VOLUME_TOO_LARGE: "volumetoolarge",
  DUPLICATE_SLICE: "duplicateslice",
};
const spacingTolerance = 0.2;
const iopTolerance = 0.01;

// Yielding to the browser between chunks of per-slice metadata work is what
// keeps the MPR spinner animating (and the viewer responsive) while a large
// series is read. Awaiting a promise that's already resolved — the common case
// once a slice is cached — only drains a microtask, which never lets the
// browser paint, so the loop has to hand back a real macrotask periodically.
const FRAME_BUDGET_MS = 16;

const makeFrameYielder = () => {
  let lastYield = Date.now();
  return async () => {
    if (Date.now() - lastYield < FRAME_BUDGET_MS) return;
    await new Promise((resolve) => setTimeout(resolve, 0));
    lastYield = Date.now();
  };
};

const PAINT_WAIT_TIMEOUT_MS = 100;

// Resolves after the browser has actually painted the pending React commit.
// requestAnimationFrame fires just BEFORE paint, so the nested setTimeout is
// what pushes us past it — without that, heavy work started here still lands
// on the main thread before the new frame reaches the screen.
export const waitForNextPaint = () =>
  new Promise((resolve) => {
    // requestAnimationFrame doesn't fire at all in a hidden or throttled tab,
    // so cap the wait — this is a "let the UI catch up" courtesy, never a
    // precondition worth wedging MPR entry on.
    const done = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, PAINT_WAIT_TIMEOUT_MS);
    requestAnimationFrame(() => setTimeout(done, 0));
  });

// The pre-flip gate below is normally a few milliseconds (its one slice is
// already cached — the viewport is displaying that series), so showing a
// loader for it unconditionally would just flash. Instead arm the top loader
// on a delay and cancel it if the gate beat the delay: feedback appears only
// in the slow case (first slice still in flight on a cold, slow network).
const SLOW_GATE_LOADER_DELAY_MS = 250;

export const startLoaderIfSlow = (ref) => {
  let started = false;
  const timer = setTimeout(() => {
    if (!ref?.current?.continuousStart) return;
    started = true;
    ref.current.continuousStart();
  }, SLOW_GATE_LOADER_DELAY_MS);

  // Only ever completes a loader this call started, so it can't cancel one
  // some other part of the app is running.
  return () => {
    clearTimeout(timer);
    if (started) ref.current?.complete?.();
  };
};

// A cheap "could this series ever be MPR'd?" gate that only touches the FIRST
// slice — modality, slice count and in-plane size are all readable from it.
// Callers run this before flipping the layout into MPR so an X-ray/scout/
// single-slice series is rejected without the 3-pane layout ever appearing,
// while the expensive whole-series geometry check (isReconstructable) runs
// afterwards, behind the MPR spinner.
export const canAttemptMPR = async (viewportIndex, viewportType) => {
  let viewport;
  if (viewportType === "viewport") {
    viewport = await getViewport(viewportIndex);
  } else {
    viewport = await getThumbnailViewport(viewportIndex);
  }

  const imageIds = viewport?.imageIds ?? [];
  if (!imageIds.length) {
    return { value: false };
  }

  const firstImage = await loadAndCacheImage(imageIds[0]);
  const multiFrame = metaData.get("multiframeModule", firstImage.imageId);
  const imagePixel = metaData.get("imagePixelModule", firstImage.imageId);
  const isMultiFrame = Number(multiFrame?.NumberOfFrames) > 1;

  // Mirrors isReconstructable's fallback: viewport.modality isn't always
  // populated, so fall back to the first slice's own DICOM metadata.
  const modality =
    viewport.modality ||
    metaData.get("generalSeriesModule", imageIds[0])?.modality;
  if (!CONSTRUCTABLE_MODALITIES.includes(modality)) {
    return { value: false };
  }

  if (!isMultiFrame && imageIds.length === 1) {
    return { value: false };
  }

  const rows = Number(imagePixel?.rows);
  const columns = Number(imagePixel?.columns);
  if (rows && columns && rows * columns * 4 > MAX_MPR_VOLUME_BYTES) {
    return { value: false, issue: reconstructionIssues.VOLUME_TOO_LARGE };
  }

  return { value: true };
};

// Per-slice metadata reads, run through a sliding window rather than an
// unbounded Promise.all over the whole series. The unbounded version invoked
// loadAndCacheImage for EVERY slice in one synchronous map() pass, which both
// jammed the main thread for the length of that pass and fired hundreds of
// concurrent loads at a request pool the prefetcher deliberately caps at
// PREFETCH_IMAGE_CONCURRENCY. Sharing that cap here keeps the parallelism win
// over the old one-at-a-time loop without the request storm, and results are
// written by index so order still matches imageIds.
export const createSeriesData = async (imageIds) => {
  const seriesData = new Array(imageIds.length);
  const yieldIfFrameBudgetExceeded = makeFrameYielder();
  let nextIndex = 0;

  const readNext = async () => {
    while (nextIndex < imageIds.length) {
      const index = nextIndex++;
      const image = await loadAndCacheImage(imageIds[index]);
      const multiFrame = metaData.get("multiframeModule", image.imageId);
      const imagePlane = metaData.get("imagePlaneModule", image.imageId);
      const imagePixel = metaData.get("imagePixelModule", image.imageId);
      seriesData[index] = {
        imageId: image.imageId,
        NumberOfFrames: multiFrame.NumberOfFrames,
        imagePlane: imagePlane,
        imagePixel: imagePixel,
        PerFrameFunctionalInformation: multiFrame.PerFrameFunctionalInformation,
        SharedFunctionalInformation: multiFrame.SharedFunctionalInformation,
      };
      await yieldIfFrameBudgetExceeded();
    }
  };

  const windowSize = Math.min(PREFETCH_IMAGE_CONCURRENCY, imageIds.length);
  await Promise.all(Array.from({ length: windowSize }, () => readNext()));
  return seriesData;
};

export const isReconstructable = async (
  viewportIndex, viewportType
) => {

  let viewport;
  if(viewportType === "viewport") {
    viewport = await getViewport(viewportIndex)
  } else {
    viewport = await getThumbnailViewport(viewportIndex);
  }
  const imageIds = viewport.imageIds;

  if (!imageIds.length) {
    return { value: false };
  }

  const seriesData = await createSeriesData(imageIds);
  const firstInstance = seriesData[0];
  const isMultiFrame = firstInstance.NumberOfFrames > 1;
  // viewport.modality isn't always populated (depends on cornerstone3D's own
  // viewport/metadata timing) — fall back to the first image's own DICOM
  // metadata, which is always available once createSeriesData has run.
  const modality =
    viewport.modality || metaData.get("generalSeriesModule", imageIds[0])?.modality;
  //   const isMultiFrame = false;
  if (!CONSTRUCTABLE_MODALITIES.includes(modality)) {
    return { value: false };
  }

  if (!isMultiFrame && seriesData.length === 1) {
    return { value: false };
  }

  // A single slice too large to fit the budget can't be fixed by k-axis
  // decimation (dropping slices) — loadMPR decimates the slice count for
  // everything else, so only this degenerate case is rejected here.
  const rows = Number(firstInstance.imagePixel?.rows);
  const columns = Number(firstInstance.imagePixel?.columns);
  if (rows && columns && rows * columns * 4 > MAX_MPR_VOLUME_BYTES) {
    return { value: false, issue: reconstructionIssues.VOLUME_TOO_LARGE };
  }

  if (
    !seriesData.every((instance) => !!instance.imagePlane.imagePositionPatient)
  ) {
    return { value: false };
  }
  const sortedSeries = sortSeries(seriesData);
  if (isMultiFrame) {
    return processMultiframe(sortedSeries[0]);
  } else {
    return processSingleframe(sortedSeries);
  }
};

const processMultiframe = (multiFrameInstance) => {
  const { PerFrameFunctionalGroupsSequence, SharedFunctionalGroupsSequence } =
    multiFrameInstance;

  // Some parsers unwrap a single-item DICOM sequence into a bare object
  // instead of a 1-element array — normalize so the [0] indexing below
  // doesn't throw on those.
  const sharedGroup = Array.isArray(SharedFunctionalGroupsSequence)
    ? SharedFunctionalGroupsSequence[0]
    : SharedFunctionalGroupsSequence;
  const perFrameGroups = Array.isArray(PerFrameFunctionalGroupsSequence)
    ? PerFrameFunctionalGroupsSequence
    : [PerFrameFunctionalGroupsSequence];

  if (!sharedGroup || !sharedGroup.PixelMeasuresSequence) {
    return { value: false };
  }

  const sharedOrientation = sharedGroup.PlaneOrientationSequence;

  if (!sharedOrientation) {
    const firstOrientation = perFrameGroups[0]?.PlaneOrientationSequence;

    if (!firstOrientation) {
      return { value: false };
    }
  }

  const frame0 = perFrameGroups[0];
  const firstPosition =
    frame0?.PlanePositionSequence || frame0?.CTPositionSequence;
  if (!firstPosition) {
    return { value: false };
  }
  // TODO - check spacing consistency

  return { value: true };
};

function processSingleframe(instances) {
  const firstImage = instances[0];
  const firstImageRows = toNumber(firstImage.imagePixel.rows);
  const firstImageColumns = toNumber(firstImage.imagePixel.columns);
  const firstImageSamplesPerPixel = toNumber(
    firstImage.imagePixel.samplesPerPixel
  );
  const firstImageOrientationPatient = toNumber(
    firstImage.imagePlane.imageOrientationPatient
  );
  const firstImagePositionPatient = toNumber(
    firstImage.imagePlane.imagePositionPatient
  );

  // Can't reconstruct with instances that have:
  // -- Different dimensions within a displaySet.
  // -- A different number of components within a displaySet.
  // -- Different orientations within a displaySet (e.g. a scout/localizer
  //    image bundled into the same series as the volumetric slices).
  // A small minority of such outliers is excluded from the volume (with a
  // warning surfaced to the user) rather than failing the whole series —
  // but if outliers are half or more of the series, there's no confident
  // "dominant" geometry to reconstruct from, so reject instead.
  const outlierIndices = [];
  for (let i = 1; i < instances.length; i++) {
    const instance = instances[i];
    const Rows = instance.imagePixel.rows;
    const Columns = instance.imagePixel.columns;
    const SamplesPerPixel = instance.imagePixel.samplesPerPixel;
    const ImageOrientationPatient = instance.imagePlane.imageOrientationPatient;

    const imageOrientationPatient = toNumber(ImageOrientationPatient);

    if (
      Rows !== firstImageRows ||
      Columns !== firstImageColumns ||
      SamplesPerPixel !== firstImageSamplesPerPixel ||
      !isSameOrientation(imageOrientationPatient, firstImageOrientationPatient)
    ) {
      outlierIndices.push(i);
    }
  }

  if (outlierIndices.length >= instances.length / 2) {
    return { value: false };
  }

  const excludedImageIds = outlierIndices.map((i) => instances[i].imageId);
  if (outlierIndices.length) {
    instances = instances.filter((_, i) => !outlierIndices.includes(i));
    if (instances.length < 2) {
      return { value: false };
    }
  }

  let missingFrames = 0;
  let hasDuplicateSlice = false;

  // Check if frame spacing is approximately equal within a spacingTolerance.
  // If spacing is on a uniform grid but we are missing frames,
  // Allow reconstruction, but pass back the number of missing frames.
  if (instances.length > 2) {
    const lastIpp = toNumber(
      instances[instances.length - 1].imagePlane.imagePositionPatient
    );

    // We can't reconstruct if we are missing ImagePositionPatient values
    if (!firstImagePositionPatient || !lastIpp) {
      return { value: false };
    }

    const averageSpacingBetweenFrames =
      getPerpendicularDistance(firstImagePositionPatient, lastIpp) /
      (instances.length - 1);

    let previousImagePositionPatient = firstImagePositionPatient;

    for (let i = 1; i < instances.length; i++) {
      const instance = instances[i];
      // Todo: get metadata from OHIF.MetadataProvider
      const imagePositionPatient = toNumber(
        instance.imagePlane.imagePositionPatient
      );

      const spacingBetweenFrames = getPerpendicularDistance(
        imagePositionPatient,
        previousImagePositionPatient
      );
      const spacingIssue = getSpacingIssue(
        spacingBetweenFrames,
        averageSpacingBetweenFrames
      );

      if (spacingIssue) {
        const issue = spacingIssue.issue;

        if (issue === reconstructionIssues.MISSING_FRAMES) {
          missingFrames += spacingIssue.missingFrames;
        } else if (issue === reconstructionIssues.DUPLICATE_SLICE) {
          // Ignored for reconstructability (not rejected) — surfaced once to
          // the user via a toast at the call site instead of logging it.
          hasDuplicateSlice = true;
        } else if (issue === reconstructionIssues.IRREGULAR_SPACING) {
          return { value: false };
        }
      }

      previousImagePositionPatient = imagePositionPatient;
    }
  }

  return { value: true, missingFrames, hasDuplicateSlice, excludedImageIds };
}

const toNumber = (val) => {
  if (Array.isArray(val)) {
    return val.map((v) => (v !== undefined ? Number(v) : v));
  } else {
    return val !== undefined ? Number(val) : val;
  }
};

const isSameOrientation = (iop1, iop2) => {
  if (iop1 === undefined || iop2 === undefined) {
    return;
  }

  return (
    Math.abs(iop1[0] - iop2[0]) < iopTolerance &&
    Math.abs(iop1[1] - iop2[1]) < iopTolerance &&
    Math.abs(iop1[2] - iop2[2]) < iopTolerance
  );
};

const getPerpendicularDistance = (a, b) => {
  return Math.sqrt(
    Math.pow(a[0] - b[0], 2) +
      Math.pow(a[1] - b[1], 2) +
      Math.pow(a[2] - b[2], 2)
  );
};

const getSpacingIssue = (spacing, averageSpacing) => {
  const equalWithinTolerance =
    Math.abs(spacing - averageSpacing) < averageSpacing * spacingTolerance;

  if (equalWithinTolerance) {
    return;
  }

  // Two instances sharing the exact same ImagePositionPatient (a duplicate/
  // redundant slice, e.g. a re-sent instance) is a real-world occurrence,
  // not "irregular spacing" — without this check it falls through to
  // numberOfSpacings = Math.round(0 / averageSpacing) = 0, and dividing by
  // that zero below always produces NaN, which always reads as irregular
  // and rejects the whole series over one harmless duplicate.
  if (spacing === 0) {
    return { issue: reconstructionIssues.DUPLICATE_SLICE };
  }

  const multipleOfAverageSpacing = spacing / averageSpacing;

  const numberOfSpacings = Math.round(multipleOfAverageSpacing);

  const errorForEachSpacing =
    Math.abs(spacing - numberOfSpacings * averageSpacing) / numberOfSpacings;

  if (errorForEachSpacing < spacingTolerance * averageSpacing) {
    return {
      issue: reconstructionIssues.MISSING_FRAMES,
      missingFrames: numberOfSpacings - 1,
    };
  }

  return { issue: reconstructionIssues.IRREGULAR_SPACING };
};
